import React from "react";
import { BoltIcon } from "@heroicons/react/24/outline";
import { ActionButton, HoverCard, joinClasses } from "./ResultsPrimitives.jsx";

function OptimizationExcludedTrucksHoverCardContent({ trucks = [] }) {
  const items = Array.isArray(trucks) ? trucks : [];

  if (!items.length) {
    return (
      <div className="grid min-w-64 gap-1.5">
        <p className="m-0 max-w-80 text-[11px] leading-[1.55] text-content-muted">
          Aucun camion mis de côté.
        </p>
      </div>
    );
  }

  return (
    <div className="grid min-w-64 gap-1.5">
      <p className="m-0 max-w-80 text-[11px] leading-[1.55] text-content-muted">
        Ces camions gardent leurs commandes. Un « Camion protégé » peut voir l'ordre de ses arrêts changer.
      </p>
      {items.map((truck) => (
        <div
          key={truck.truckId || truck.truckLabel}
          className="grid gap-px rounded-[12px] border border-black/6 bg-slate-50/80 px-3 py-2"
        >
          <div className="flex min-w-0 items-center justify-between gap-2">
            <span className="min-w-0 truncate text-[11px] font-semibold leading-[1.35] text-content-ink">{truck.truckLabel}</span>
            {truck.refreshStatus === "failed" ? (
              <span className="inline-flex shrink-0 items-center rounded-full border border-[rgba(217,119,6,0.24)] bg-[rgba(255,247,237,0.96)] px-1.5 py-0.5 text-[8.5px] font-black uppercase tracking-[0.11em] text-[rgb(154,52,18)]">
                Non mis à jour
              </span>
            ) : null}
          </div>
          {truck.reasonLabel ? (
            <span className="text-[10px] leading-[1.4] text-content-muted">{truck.reasonLabel}</span>
          ) : null}
          {truck.refreshStatus === "failed" && (truck.refreshReasonLabel || truck.refreshReasonDetail) ? (
            <span className="mt-1 text-[10px] leading-[1.45] text-[rgb(154,52,18)]">
              {truck.refreshReasonLabel || "Mise à jour impossible"}
              {truck.refreshReasonDetail ? ` · ${truck.refreshReasonDetail}` : ""}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export default function OptimizationModeSplitButton({
  cancelLabel = "Arrêter",
  cancelLoadingLabel = "Annulation demandée...",
  disabled = false,
  disabledTitle = "",
  excludedTrucks = [],
  isCancelling = false,
  isFinalizing = false,
  isLoading = false,
  label = "Optimiser",
  loadingLabel = "Optimisation en cours...",
  onCancel = null,
  onLaunch = null,
  periodMenu = null,
  showCancel = false,
  statusNotice = null,
}) {
  const canRequestCancel = showCancel && typeof onCancel === "function";
  const isPrimaryCancelAction = isLoading && canRequestCancel;
  const isPrimaryFinalization = isLoading && isFinalizing;
  const primaryButtonDisabled = disabled || (isLoading
    ? (!canRequestCancel || isCancelling || isFinalizing)
    : false);
  const primaryButtonLoadingLabel = isPrimaryFinalization
    ? "Finalisation…"
    : isCancelling
      ? cancelLoadingLabel
      : loadingLabel;
  const excludedTruckItems = Array.isArray(excludedTrucks) ? excludedTrucks.filter(Boolean) : [];
  const hasExcludedTrucks = excludedTruckItems.length > 0;
  const excludedTrucksHoverContent = <OptimizationExcludedTrucksHoverCardContent trucks={excludedTruckItems} />;
  const excludedTrucksHoverTitle = hasExcludedTrucks
    ? "Camions exclus de l'optimisation"
    : "Aucun camion exclu";
  const safeStatusNotice = statusNotice && typeof statusNotice === "object"
    ? statusNotice
    : null;
  const showStatusNotice = !isLoading && safeStatusNotice?.content != null;
  const statusNoticeTitle = typeof safeStatusNotice?.title === "string" && safeStatusNotice.title.trim()
    ? safeStatusNotice.title.trim()
    : "État du Dispatch";
  const statusNoticeAriaLabel = typeof safeStatusNotice?.ariaLabel === "string" && safeStatusNotice.ariaLabel.trim()
    ? safeStatusNotice.ariaLabel.trim()
    : "Afficher le détail de l’état du Dispatch";

  const optimizeLaunchButton = (
    <ActionButton
      tone="optimizer"
      size="sm"
      className={joinClasses([
        "h-8.25! min-h-8.25! px-3.75! text-[0.95rem]!",
        showStatusNotice || (!isLoading && periodMenu) ? "rounded-l-[4px]! rounded-r-none!" : "rounded-[4px]!",
      ])}
      aria-label={isPrimaryCancelAction
        ? "Optimisation en cours. Cliquez à nouveau pour demander l'arrêt."
        : (isPrimaryFinalization ? "Finalisation de l'optimisation en cours." : undefined)}
      disabled={primaryButtonDisabled}
      isLoading={isLoading}
      loadingLabel={primaryButtonLoadingLabel}
      title={isPrimaryCancelAction
        ? "Cliquer à nouveau pour demander l'arrêt de l'optimisation"
        : (isPrimaryFinalization
          ? "La sauvegarde finale de l'optimisation est en cours."
          : (disabled && !isLoading ? disabledTitle || undefined : undefined))}
      onClick={isPrimaryCancelAction ? onCancel : onLaunch}
    >
      <span className="inline-flex items-center gap-2 whitespace-nowrap">
        {!isLoading ? <BoltIcon aria-hidden="true" className="h-5 w-5 shrink-0" strokeWidth={2.5} /> : null}
        {isPrimaryCancelAction ? cancelLabel : label}
      </span>
    </ActionButton>
  );

  return (
    <div className="flex min-w-0 items-start justify-end">
      <div
        aria-label="Actions d’optimisation du Dispatch"
        className="inline-flex min-w-0 items-stretch"
        role="group"
      >
        {!isLoading ? (
          <HoverCard
            title={excludedTrucksHoverTitle}
            content={excludedTrucksHoverContent}
            placement="bottom-end"
            delay={140}
            maxWidth={360}
            accentColor={hasExcludedTrucks ? "var(--hovercard-accent-warning)" : "var(--hovercard-accent-neutral)"}
            referenceAsChild
          >
            {optimizeLaunchButton}
          </HoverCard>
        ) : optimizeLaunchButton}

        {!isLoading ? periodMenu : null}
        {showStatusNotice ? (
          <HoverCard
            title={statusNoticeTitle}
            content={safeStatusNotice.content}
            placement="bottom-end"
            delay={140}
            maxWidth={400}
            accentColor="var(--hovercard-accent-warning)"
            referenceAsChild
          >
            <button
              type="button"
              aria-label={statusNoticeAriaLabel}
              className="-ml-px inline-flex h-8.25 min-h-8.25 w-7.5 shrink-0 items-center justify-center rounded-l-none rounded-r-[4px] border border-amber-300 bg-amber-50 text-[0.95rem] font-black leading-none text-amber-800 transition hover:border-amber-400 hover:bg-amber-100 hover:text-amber-950 focus-visible:relative focus-visible:z-1 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-amber-400/60 focus-visible:ring-offset-2"
              data-optimization-status-notice="true"
            >
              <span aria-hidden="true">!</span>
            </button>
          </HoverCard>
        ) : null}
      </div>
    </div>
  );
}
